import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import { isArray, omit } from 'lodash'

import { getQueryRefetchOptions } from 'modules/Monitoring/consts/query/query'
import { QueryKey } from 'modules/Monitoring/consts/query/queryKey'
import {
  AgentAttendanceDetails,
  AgentAttendanceRequestParams,
  AgentAttendanceShiftStatusTimelineResponse,
  AgentAttendanceShort,
  AgentsAttendanceShiftStatusTimelineRequest,
  AgentsAttendanceShortRequest,
  AgentTicketsRequest,
  QueueHealthAnalyticsResponse,
  QueuesHealthDetailsRequestParams,
  QueueStatsFilterRequestParams,
  QueueStatsFilterResponse,
  SpecialisationAttendanceAggregated,
  TicketsRequestParams,
  TicketsVolumeRequestParams,
  VolumeAggregationDimensionExtended,
  VolumeBreakdownResponse,
} from 'modules/Monitoring/types'

import {
  getAgentAttendanceDetails,
  getAgentAttendanceIntervals,
  getAgentAttendanceShort,
  getAgentAttendanceTimeline,
  getAssignedTicketIds,
  getAssignedTickets,
  getQueuesHealthDetails,
  getQueueStatsFilter,
  getTicketIds,
  getTicketOutcomesVolume,
  getTickets,
  getTicketsVolume,
} from './requests'
import { unpackTicketsResponsePages } from './utils'

export const useTicketsVolumeQuery = (
  params: TicketsVolumeRequestParams,
  options?: Partial<UseQueryOptions<VolumeBreakdownResponse>>,
) => {
  return useQuery({
    queryKey: [QueryKey.GetTicketsVolume, omit(params, 'scale')],
    queryFn: () => {
      return params.dimension === VolumeAggregationDimensionExtended.Outcomes
        ? getTicketOutcomesVolume(params)
        : getTicketsVolume(params)
    },
    placeholderData: keepPreviousData,
    ...options,
  })
}

export const useAgentAttendanceDetailsQuery = (
  params: AgentAttendanceRequestParams,
  options?: Partial<UseQueryOptions<AgentAttendanceDetails[]>>,
) => {
  return useQuery({
    queryKey: [QueryKey.GetAgentAttendanceDetails, params],
    queryFn: () => getAgentAttendanceDetails(params),
    ...getQueryRefetchOptions<AgentAttendanceDetails[]>(),
    ...options,
  })
}

export const useAgentAttendanceShortQuery = (
  params: AgentsAttendanceShortRequest,
  options?: Partial<UseQueryOptions<AgentAttendanceShort[]>>,
) => {
  return useQuery({
    queryKey: [QueryKey.GetAgentAttendanceDetails, params],
    queryFn: () => getAgentAttendanceShort(params),
    ...getQueryRefetchOptions<AgentAttendanceShort[]>(),
    ...options,
  })
}

export const useAgentAttendanceIntervalsQuery = (
  params: AgentAttendanceRequestParams,
) => {
  return useQuery({
    queryKey: [QueryKey.GetAgentAttendanceIntervals, params],
    queryFn: () => getAgentAttendanceIntervals(params),
    placeholderData: keepPreviousData,
    ...getQueryRefetchOptions<SpecialisationAttendanceAggregated[]>(),
  })
}

export const useAgentAttendanceTimelineQuery = (
  params: AgentsAttendanceShiftStatusTimelineRequest,
  options?: Partial<UseQueryOptions<AgentAttendanceShiftStatusTimelineResponse>>,
) => {
  return useQuery({
    queryKey: [QueryKey.GetAgentAttendanceTimeline, params],
    queryFn: () => getAgentAttendanceTimeline(params),
    placeholderData: keepPreviousData,
    ...options,
    ...getQueryRefetchOptions<AgentAttendanceShiftStatusTimelineResponse>(),
  })
}

export const useQueuesHealthDetailsQuery = (params: QueuesHealthDetailsRequestParams) => {
  return useQuery({
    queryKey: [QueryKey.GetQueuesHealthDetails, params],
    queryFn: () => getQueuesHealthDetails(params),
    ...getQueryRefetchOptions<QueueHealthAnalyticsResponse>(),
  })
}

const DEFAULT_TICKETS_OFFSET = 50
export const useTicketsInfiniteQuery = (
  params: TicketsRequestParams,
  enabled?: boolean,
) => {
  return useInfiniteQuery({
    queryKey: [QueryKey.GetTickets, params],
    queryFn: ({ pageParam }) =>
      getTickets({
        ...params,
        page: {
          ...params.page,
          limit: DEFAULT_TICKETS_OFFSET,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (isArray(lastPage)) {
        return undefined
      }

      if (lastPage?.items.length < DEFAULT_TICKETS_OFFSET) {
        return undefined
      }

      return lastPageParam + DEFAULT_TICKETS_OFFSET
    },
    select: unpackTicketsResponsePages,
    enabled,
  })
}

export const useAssignedTicketsInfiniteQuery = (
  params: AgentTicketsRequest,
  enabled?: boolean,
) => {
  return useInfiniteQuery({
    queryKey: [QueryKey.GetAssignedTickets, params],
    queryFn: ({ pageParam }) =>
      getAssignedTickets({
        ...params,
        page: {
          ...params.page,
          limit: DEFAULT_TICKETS_OFFSET,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (isArray(lastPage)) {
        return undefined
      }

      if (lastPage?.items.length < DEFAULT_TICKETS_OFFSET) {
        return undefined
      }

      return lastPageParam + DEFAULT_TICKETS_OFFSET
    },
    select: unpackTicketsResponsePages,
    enabled,
  })
}

export const useQueueStatsFilterQuery = (
  params: QueueStatsFilterRequestParams,
  options?: Partial<UseQueryOptions<QueueStatsFilterResponse>>,
) => {
  return useQuery({
    queryKey: [QueryKey.GetQueueStatsFilter, params],
    queryFn: () => getQueueStatsFilter(params),
    ...options,
  })
}

export const useTicketIdsMutation = () => {
  return useMutation({
    mutationFn: (params: TicketsRequestParams) => getTicketIds(params),
  })
}

export const useAssignedTicketIdsMutation = () => {
  return useMutation({
    mutationFn: (params: AgentTicketsRequest) => getAssignedTicketIds(params),
  })
}
